import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import { SEARCH_QUERY } from '../../queries/SEARCH_QUERY';
import { useSanitizer } from '../../../components/common/learningItem/sanitizers';
import { USER_DETAILS_QUERY } from '../../queries/USER_DETAILS_QUERY';
import { useTranslations } from '../../../hooks/translations';
import { ACTIVE_BRAND_UUID } from '../../../branding';
import { ENROLLMENTS_QUERY } from '../../queries/ENROLLMENTS_QUERY';
import { Routes } from '../../../navigation';
import { apolloService, authService } from '../../../services';
import { MINIMAL_SEARCH_QUERY } from '../../queries/MINIMAL_SEARCH_QUERY';

function getLocalized( array, loc ) { // this is probably duplicated somewhere else but no time to check
    if ( !array || array.length == 0 ) {return '';}
    return ( array.find( a => a.locale == loc ) ?? array.find( a => a.locale == 'en-US' ) ?? array[ 0 ] ).content;
}

export function useSearchLazyQuery( options, syncResources = true, syncParents = true ) {
    const { data: userData, loading: userDataLoading } = useQuery( USER_DETAILS_QUERY );
   
    const [ query, { data: rawData, loading, error, refetch: refetchSearch } ] = useLazyQuery( SEARCH_QUERY, {
        ...options,
        fetchPolicy: 'network-only',
    } );
    const { data: enrollmentsData, loading: enrollmentsLoading, refetch: refetchEnrollments } = useQuery( ENROLLMENTS_QUERY, { variables: { brandId: ACTIVE_BRAND_UUID } } );

    const [ queuedQuery, setQueuedQuery ] = useState( undefined );
    const { locale } = useTranslations();
    const [ refinedData, setRefinedData ] = useState( null );
    const [ pageData, setPageData ] = useState( {}  );
    const [ prereqsLoading, setPrereqsLoading ] = useState( true );

    const { sanitizeCourses, sanitizeCurricula, sanitizeResources, sanitizeBasic } = useSanitizer();

    async function evaluatePrereqs( data ) {
        if ( !data || data.length == 0 ) {return;}

        /**
         * for courses 
         *      iterate courses
         *          get object ids
         *          cross reference with enrollments on if they are comleted
         *          determine locked/unlocked
         * 
         * for curriculums
         *      iterate curriculums
         *          get all prereq object ids := {obj}
         *      search for the {obj} curriculums := {prereqs}
         *      iterate {prereqs}
         *          cross reference with enrollments on if they are completed
         *      iterate curriculums
         *          cross reference prereq object ids with {prereqs} to determine locked/unlocked
         *      
         */

        const enrollments = enrollmentsData?.enrollments?.edges?.map( e => e.node ) ?? [];
        const getPrereqInfo = ( courseId ) => ( {
                id: courseId,
                completed: enrollments.find( e => e.courseId == courseId )?.status == 'COMPLETED',
                link: Routes.CourseDetails.buildPath( courseId ),
        } );

        // get all the curriculum ids i need to request
        const prereqCurrIds = data.filter( d => d.type == 'curriculum' )
            .flatMap( c => c.lxp?.prerequisiteSet?.prerequisites ?? [] )
            .map( c => c.objectId );

        // make the prereq curr search request
        let prereqCurrs = [];
        if ( prereqCurrIds.length > 0 ) {
            await apolloService.query( {
                query: SEARCH_QUERY,
                variables: {
                    ids: prereqCurrIds,
                    page: 1,
                    limit: prereqCurrIds.length,
                    userId: authService.isAdminUser() ? undefined : userData?.person.id,
                    types: [ 'curricula' ],
                },
                context: { headers: { authorization: `Bearer ${ authService.getAccessToken() }` } },
                fetchPolicy: 'network-only'
            } ).then( currs => {
                prereqCurrs = ( currs?.data?.search?.edges?.map( e => e.node ) ?? [] )
                    .map( c => ( {
                        ...c,
                        id: c.curriculumId,
                        name: getLocalized( c.name, locale ),
                        courses: c.courses.map( co => getPrereqInfo( co.courseId ) ),
                        link: Routes.CurriculumDetails.buildPath( c.curriculumId ),
                    } ) )
                    .map( c => ( {
                        ...c,
                        completed: c.courses.every( co => co.completed )
                    } ) );
            } );
        }

        function getPrereqs( item ) {
            if ( item.type == 'course' ) {
                return item.lxp?.prerequisiteSet?.prerequisites
                    .map( p => ( { 
                        type: item.type,
                        name: getLocalized( p.name, locale ),
                        ...getPrereqInfo( p.objectId )
                    } ) );
            }
    
            if ( item.type == 'curriculum' ) {
                return item.lxp?.prerequisiteSet?.prerequisites
                    .map( p => prereqCurrs.find( c => c.id == p.objectId ) );
            }
    
            return null;
        }

        function lockCourse( course, prerequisiteData ) {
            const locked = prerequisiteData.locked;
            if ( !locked ) {
                return course;
            }
            return {
                ...course,
                prerequisiteData,
                learningObjects: course.learningObjects?.map( l => ( { ...l, prerequisiteData: { locked: true } } ) ),
                resources: course.resources?.map( r => ( { ...r, prerequisiteData: { locked: true } } ) ),
            };
        }

        function lockCurriculum( curr, prerequisiteData ) {
            const locked = prerequisiteData.locked;
            return {
                ...curr,
                prerequisiteData,
                resources: curr.resources?.map( r => ( { ...r, prerequisiteData: { locked } } ) ),
                courses: curr.courses?.map( c => 
                    locked ? {
                            ...c,
                            prerequisiteData: { locked: true },
                            learningObjects: c.learningObjects?.map( l => ( { ...l, prerequisiteData: { locked: true } } ) ),
                            resources: c.resources?.map( r => ( { ...r, prerequisiteData: { locked: true } } ) ),
                        } :
                    c.prerequisiteData?.locked ? {
                            ...c,
                            prerequisiteData: c.prerequisiteData,
                            learningObjects: c.learningObjects?.map( l => ( { ...l, prerequisiteData: { locked: true } } ) ),
                            resources: c.resources?.map( r => ( { ...r, prerequisiteData: { locked: true } } ) ),
                        } : c )
            };
        }

        const refData = await Promise.all( data.map( async d => {
            const prereqs = d.lxp?.prerequisiteSet?.prerequisites ? getPrereqs( d ) : null;
    
            if ( d.type == 'curriculum' ) {
                d.courses = await evaluatePrereqs( d.courses );
            }
    
            const prereqData = !prereqs ? { locked: false } : {
                prerequisites: prereqs,
                operator: d.lxp?.prerequisiteSet?.operator,
                locked: d.lxp?.prerequisiteSet?.operator == 'AND' ? prereqs.some( p => !p.completed ) : !prereqs.some( p => p.completed )
            };
    
            const lockedItem = d.type == 'curriculum' ? lockCurriculum( d, prereqData ) : lockCourse( d, prereqData );
    
            return Promise.resolve( lockedItem );
        } ) );

        setPrereqsLoading( false );
        setRefinedData( refData );

        return refData;
    }

    function refetch() {
        refetchSearch();
        refetchEnrollments();
    }

    const fetchCertificateUrl = ( courseId ) => ( async ( ) => {
        return await apolloService.query( {
            query: ENROLLMENTS_QUERY,
            context: { headers: { authorization: `Bearer ${ authService.getAccessToken() }` } },
            fetchPolicy: 'network-only'
        } ).then( e => {
            return e.data?.enrollments?.edges?.map( e => e.node )
                .find( d => d.courseId == courseId )
                .userCertificate?.userCertificateUrl;
        } );
    } );

    const refineSearchData = async ( data ) => {
        if ( !data?.search?.edges ) {return [];}

        const enrollments = enrollmentsData?.enrollments?.edges?.map( e => e.node ) ?? [];
    
        let refinedData = data.search.edges
            .map( e => e.node )
            .map( e => 
                e.__typename === 'ES_Course' ? sanitizeCourses( [ {
                    ...e,
                    type: 'course',
                    id: e.courseId,
                    enrollment: enrollments.find( enr => enr.courseId == e.courseId ),
                    courseCertificates: e.lxp?.courseCertificates,
                    fetchCertificateUrl: fetchCertificateUrl( e.courseId ),
                } ], { syncResources, syncParents } )[ 0 ] :
                e.__typename === 'ES_Resource' ? sanitizeResources( [ {
                    ...e,
                    type: 'resource',
                    id: e.resourceId,
                } ] )[ 0 ] :
                e.__typename === 'ES_Curriculum' ? sanitizeCurricula( [ {
                    ...e,
                    type: 'curriculum',
                    id: e.curriculumId,
                    courses: e.courses?.map( c => ( { id: c.courseId, enrollment: enrollments.find( enr => enr.courseId == c.courseId ), ...c  } ) ) ?? []
                } ], { syncResources, syncParents } )[ 0 ] :
            e )
            .filter( e => e );

        const currIds = refinedData.flatMap( d => {
            if ( d.type == 'course' ) {
                return d.parentIds;
            }
            return [];
        } );

        if ( currIds.length > 0 && syncParents ) {
            await apolloService.query( {
                query: MINIMAL_SEARCH_QUERY,
                variables: {
                    types: [ 'curricula' ],
                    ids: currIds,
                    query: '',
                    page: 1,
                    limit: currIds.length, 
                    personId: authService.isAdminUser() ? undefined : userData?.person.id,
                },
                context: { headers: { authorization: `Bearer ${ authService.getAccessToken() }` } },
                fetchPolicy: 'network-only'
            } ).then( currs => {

                const curricula = currs.data.search.edges.map( c => sanitizeBasic( c ) );
                
                refinedData = refinedData.map( d => {
                    if ( d.type == 'course' ) {
                        d.parents = d.parentIds.map( p => curricula.find( c => c.curriculumId == p ) ).filter( p => !!p );
                    }
                    return d;
                } );
            } );
        }

        return refinedData;
    };

    async function search( variables ) {
        const formattedLocale = locale?.replace( '-', '_' );
        setRefinedData( null );

        if ( userDataLoading || !userData?.person.id ) {
            // if user data hasnt loaded yet queue up the search
            // weird react syntax for putting an inline func into a state 
            setQueuedQuery( () => ( id ) => query( { 
                variables: {
                    ...variables,
                    localeCode: formattedLocale,
                    personId: authService.isAdminUser() ? undefined : id,
                }
            } ) ); 
        } else {
            return query( {
                variables: {
                    ...variables,
                    localeCode: formattedLocale,
                    personId: authService.isAdminUser() ? undefined : userData?.person.id,
                }
            } );
        }
    }

    useEffect( () => {
        if ( queuedQuery && !userDataLoading && userData?.person?.id  ) {
            queuedQuery( userData.person.id );
            setQueuedQuery( undefined );
        }
    }, [ userDataLoading, userData ] );

    useEffect( () => {
        if ( !rawData || rawData.length == 0 || !enrollmentsData ) {return;}
        setPrereqsLoading( true );
        ( async () => {
            setPageData( {
                totalResults: rawData?.search.totalCount,
                ...rawData?.search.searchOptions,
            } );

            if ( rawData.search.totalCount == 0 ) {
                setPrereqsLoading( false );
            }
    
            const refData = await refineSearchData( rawData || [] );
    
            setRefinedData( refData );
            evaluatePrereqs( refData );
        } )();
    }, [ rawData, enrollmentsData ] );

    return useMemo ( () => {
        // dont segment the data because its already segmented on the backend
        return [ search, {
            error: ( error ),
            loading: loading || enrollmentsLoading || prereqsLoading,
            data: refinedData,
            pageData,
            enrollments: enrollmentsData,
            refetch,
        } ];
    }, [ error, loading, refinedData, enrollmentsData, prereqsLoading ] );
}

export function useMinimalSearchLazyQuery( options ) {

    const [ query,  { data: rawData, loading, error } ] = useLazyQuery( MINIMAL_SEARCH_QUERY, {
        ...options,
        fetchPolicy: 'network-only',
    } );

    const { locale } = useTranslations();

    const refineSearchData = ( data ) => {
        if ( !data?.search?.edges ) {return [];}
    
        return data.search.edges
            .map( e => e.node )
            .filter( e => e && e.name.some( n => n.locale == locale ) )
            .map( e => ( { 
                ...e,
                name: e.name.find( n => n.locale == locale ).content,
                description: e.description.find( n => n.locale == locale ).content,
            } ) )
            .map( e => 
                e.__typename === 'ES_Course' ? {
                    ...e,
                    type: 'course',
                    id: e.courseId,
                } :
                e.__typename === 'ES_Resource' ? {
                    ...e,
                    type: 'resource',
                    id: e.resourceId,
                } :
                e.__typename === 'ES_Curriculum' ? {
                    ...e,
                    type: 'curriculum',
                    id: e.curriculumId,
                } :
            e )
            .filter( e => e );
    };

    function search( variables ) {
        query( { variables } );
    }

    return useMemo ( () => {
        let pageData = {
            totalResults: rawData?.search.totalCount,
            ...rawData?.search.searchOptions,
        };

        let refinedData = rawData === undefined ? null : refineSearchData( rawData );
        
        // dont segment the data because its already segmented on the backend
        return [ search, {
            error: ( error ),
            loading: loading,
            data: refinedData,
            pageData,
        } ];
    }, [ error, loading, rawData, locale, search ] );
}
